import type { AxiosPromise } from 'axios';
import BaseClient, { type ExtendedAxiosRequestConfig } from './BaseClient';
import type {
    SsoConfig,
    SsoConnection,
    SsoConnectionInput,
    SsoDiscoverResponse,
    SsoEmailDomain,
    SsoVerifiedDomains,
} from './ssoClient.types';

class SsoClient extends BaseClient {
    discover(
        email: string,
        recaptchaToken: string | undefined,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<SsoDiscoverResponse> {
        return this.makeCoreApiRequest(
            '/sso/discover',
            'post',
            { email, 'g-recaptcha-response': recaptchaToken },
            null,
            config
        );
    }

    getConfig(teamId: number): AxiosPromise<SsoConfig> {
        const url = `/api/v3/teams/${teamId}/sso/config`;
        return this.makeCoreApiRequest(url);
    }

    updateConfig(teamId: number, data: { require_sso: boolean }): AxiosPromise<SsoConfig> {
        const url = `/api/v3/teams/${teamId}/sso/config`;
        return this.makeCoreApiRequest(url, 'put', data);
    }

    getConnection(teamId: number): AxiosPromise<SsoConnection> {
        const url = `/api/v3/teams/${teamId}/sso/connection`;
        return this.makeCoreApiRequest(url);
    }

    updateConnection(teamId: number, data: SsoConnectionInput): AxiosPromise<SsoConnection> {
        const url = `/api/v3/teams/${teamId}/sso/connection`;
        return this.makeCoreApiRequest(url, 'put', data);
    }

    deleteConnection(teamId: number): AxiosPromise<void> {
        const url = `/api/v3/teams/${teamId}/sso/connection`;
        return this.makeCoreApiRequest(url, 'delete');
    }

    getEmailDomains(teamId: number): AxiosPromise<{ data: SsoEmailDomain[] }> {
        const url = `/api/v3/teams/${teamId}/sso/email-domains`;
        return this.makeCoreApiRequest(url);
    }

    createEmailDomain(teamId: number, data: { domain: string }): AxiosPromise<SsoEmailDomain> {
        const url = `/api/v3/teams/${teamId}/sso/email-domains`;
        return this.makeCoreApiRequest(url, 'post', data);
    }

    verifyEmailDomain(teamId: number, emailDomainId: number): AxiosPromise<SsoEmailDomain> {
        const url = `/api/v3/teams/${teamId}/sso/email-domains/${emailDomainId}/verify`;
        return this.makeCoreApiRequest(url, 'post');
    }

    deleteEmailDomain(teamId: number, emailDomainId: number): AxiosPromise<void> {
        const url = `/api/v3/teams/${teamId}/sso/email-domains/${emailDomainId}`;
        return this.makeCoreApiRequest(url, 'delete');
    }

    getVerifiedDomains(teamId: number): AxiosPromise<SsoVerifiedDomains> {
        const url = `/api/v3/teams/${teamId}/sso/verified-domains`;
        return this.makeCoreApiRequest(url);
    }
}

export default new SsoClient();
